<template>
  <div class="restaurant-settings-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="restaurant-settings-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="title-icon">
                  <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                </svg>
                Restaurant Settings
              </h1>
              <p class="page-subtitle">Configure your WhatsApp restaurant ordering system</p>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading settings...</p>
          </div>

          <!-- Settings Form -->
          <div v-else class="settings-form">
            <!-- Basic Information Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                </svg>
                Basic Information
              </h3>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Restaurant Name (English) *</label>
                  <input
                    v-model="settings.restaurant_name"
                    type="text"
                    placeholder="e.g., Pizza Palace"
                    class="form-input"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">Restaurant Name (Arabic)</label>
                  <input
                    v-model="settings.restaurant_name_ar"
                    type="text"
                    placeholder="e.g., بيتزا بالاس"
                    class="form-input"
                    dir="rtl"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Description (English)</label>
                  <textarea
                    v-model="settings.restaurant_description"
                    placeholder="Brief description of your restaurant..."
                    class="form-textarea"
                    rows="3"
                  ></textarea>
                </div>

                <div class="form-group">
                  <label class="form-label">Description (Arabic)</label>
                  <textarea
                    v-model="settings.restaurant_description_ar"
                    placeholder="وصف موجز لمطعمك..."
                    class="form-textarea"
                    rows="3"
                    dir="rtl"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Delivery Settings Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18,18.5A1.5,1.5 0 0,1 16.5,17A1.5,1.5 0 0,1 18,15.5A1.5,1.5 0 0,1 19.5,17A1.5,1.5 0 0,1 18,18.5M19.5,9.5L21.46,12H17V9.5M6,18.5A1.5,1.5 0 0,1 4.5,17A1.5,1.5 0 0,1 6,15.5A1.5,1.5 0 0,1 7.5,17A1.5,1.5 0 0,1 6,18.5M20,8H17V4H3C1.89,4 1,4.89 1,6V17H3A3,3 0 0,0 6,20A3,3 0 0,0 9,17H15A3,3 0 0,0 18,20A3,3 0 0,0 21,17H23V12L20,8Z"/>
                </svg>
                Delivery Settings
              </h3>

              <div class="form-group">
                <div class="toggle-group">
                  <div class="toggle-label-text">
                    <span class="toggle-title">Enable Delivery</span>
                    <span class="toggle-description">Allow customers to place delivery orders</span>
                  </div>
                  <label class="toggle-switch">
                    <input
                      v-model="settings.delivery_enabled"
                      type="checkbox"
                      class="toggle-input"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Delivery Fee (EGP)</label>
                  <input
                    v-model.number="settings.delivery_fee"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    class="form-input"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">Minimum Order Amount (EGP)</label>
                  <input
                    v-model.number="settings.min_order_amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    class="form-input"
                  />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Cart Expiry Time</label>
                <select v-model.number="settings.cart_expiry_minutes" class="form-select">
                  <option :value="30">30 Minutes</option>
                  <option :value="60">60 Minutes</option>
                  <option :value="90">90 Minutes</option>
                </select>
                <small class="form-hint">How long items stay in customer's cart</small>
              </div>

              <div class="form-group">
                <div class="toggle-group">
                  <div class="toggle-label-text">
                    <span class="toggle-title">Send Receipt Image on Delivery</span>
                    <span class="toggle-description">Automatically send a receipt image to customers when order is delivered</span>
                  </div>
                  <label class="toggle-switch">
                    <input
                      v-model="settings.send_receipt_on_delivery"
                      type="checkbox"
                      class="toggle-input"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Branch Settings Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"/>
                </svg>
                Branch Settings
              </h3>

              <div class="form-group">
                <div class="toggle-group">
                  <div class="toggle-label-text">
                    <span class="toggle-title">Enable Multi-Branch Mode</span>
                    <span class="toggle-description">Uses your departments as restaurant branches</span>
                  </div>
                  <label class="toggle-switch">
                    <input
                      v-model="settings.multi_branch_enabled"
                      type="checkbox"
                      class="toggle-input"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Welcome Messages Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,3C6.5,3 2,6.58 2,11A7.17,7.17 0 0,0 4.22,16.22C3.25,18.22 2,18.73 2,18.73C2,18.73 6.16,18.93 9.37,17.36C10.23,17.61 11.11,17.73 12,17.73C17.5,17.73 22,14.15 22,9.73C22,5.31 17.5,3 12,3M12,15.73C7.5,15.73 4,12.84 4,9.23C4,5.62 7.5,3 12,3C16.5,3 20,5.88 20,9.5C20,13.12 16.5,15.73 12,15.73Z"/>
                </svg>
                Welcome Messages
              </h3>

              <div class="form-group">
                <label class="form-label">Welcome Message (English)</label>
                <textarea
                  v-model="settings.welcome_message"
                  placeholder="Welcome to our restaurant! Browse our menu and order easily."
                  class="form-textarea"
                  rows="3"
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Welcome Message (Arabic)</label>
                <textarea
                  v-model="settings.welcome_message_ar"
                  placeholder="مرحباً بك في مطعمنا! تصفح قائمتنا واطلب بسهولة."
                  class="form-textarea"
                  rows="3"
                  dir="rtl"
                ></textarea>
              </div>
            </div>

            <!-- Save Button -->
            <div class="form-actions">
              <button @click="saveSettings" class="btn-primary" :disabled="saving">
                <svg v-if="!saving" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z"/>
                </svg>
                <div v-else class="button-spinner"></div>
                {{ saving ? 'Saving...' : 'Save Settings' }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { authStore } from '@/store/auth'

export default {
  name: 'RestaurantSettingsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: false,
      saving: false,
      settings: {
        restaurant_name: '',
        restaurant_name_ar: '',
        restaurant_description: '',
        restaurant_description_ar: '',
        delivery_enabled: true,
        delivery_fee: 0,
        min_order_amount: 0,
        cart_expiry_minutes: 30,
        send_receipt_on_delivery: false,
        multi_branch_enabled: false,
        welcome_message: '',
        welcome_message_ar: ''
      }
    }
  },
  async mounted () {
    // Ensure restaurant mode is set in authStore when this page loads
    await this.checkRestaurantMode()
    this.loadSettings()
  },
  methods: {
    async checkRestaurantMode () {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant/mode/check', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          authStore.setRestaurantMode(data.restaurant_mode || false)
        }
      } catch (error) {
        console.error('Error checking restaurant mode:', error)
      }
    },

    async loadSettings () {
      try {
        this.loading = true
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant-mode/settings', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success && data.settings) {
          // Convert integer 1/0 to boolean true/false for toggle switches
          const settings = {
            ...data.settings,
            delivery_enabled: Boolean(data.settings.delivery_enabled),
            send_receipt_on_delivery: Boolean(data.settings.send_receipt_on_delivery),
            multi_branch_enabled: Boolean(data.settings.multi_branch_enabled)
          }
          this.settings = { ...this.settings, ...settings }
        }
      } catch (error) {
        console.error('Error loading settings:', error)
        alert('Failed to load settings')
      } finally {
        this.loading = false
      }
    },

    async saveSettings () {
      try {
        this.saving = true
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant-mode/settings', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.settings)
        })

        const data = await response.json()
        if (data.success) {
          alert('Settings saved successfully!')
        } else {
          alert('Failed to save settings: ' + (data.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error saving settings:', error)
        alert('Failed to save settings')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.restaurant-settings-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.restaurant-settings-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  background: var(--bg-primary);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.page-title {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  color: #3b82f6;
}

.page-subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--text-secondary);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Settings Form */
.settings-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.settings-card {
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-primary);
}

.card-title {
  margin: 0 0 24px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--color-border);
}

/* Form Elements */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  font-family: inherit;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

/* Toggle Switch Styles */
.toggle-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: var(--bg-secondary);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.toggle-group:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.toggle-label-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.toggle-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

.toggle-description {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 400;
}

/* Toggle Switch Component */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 72px;
  height: 40px;
  flex-shrink: 0;
  cursor: pointer;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  border-radius: 40px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-slider:before {
  content: "";
  position: absolute;
  height: 32px;
  width: 32px;
  left: 4px;
  top: 4px;
  background: white;
  border-radius: 50%;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.toggle-input:checked + .toggle-slider {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(59, 130, 246, 0.4),
    0 0 0 4px rgba(59, 130, 246, 0.1);
}

.toggle-input:checked + .toggle-slider:before {
  transform: translateX(32px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.toggle-switch:hover .toggle-slider {
  transform: scale(1.02);
}

.toggle-input:focus + .toggle-slider {
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 0 0 4px rgba(59, 130, 246, 0.2);
}

.toggle-input:checked:focus + .toggle-slider {
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(59, 130, 246, 0.4),
    0 0 0 4px rgba(59, 130, 246, 0.3);
}

/* Dark mode support for toggles */
@media (prefers-color-scheme: dark) {
  .toggle-slider {
    background: linear-gradient(135deg, #475569 0%, #334155 100%);
  }

  .toggle-input:checked + .toggle-slider {
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  }
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    padding: 16px;
  }

  .header-content {
    padding: 20px;
  }

  .page-title {
    font-size: 24px;
  }

  .settings-card {
    padding: 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .toggle-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px;
  }

  .toggle-label-text {
    width: 100%;
  }

  .toggle-switch {
    align-self: flex-end;
  }
}
</style>
